import BarnesHutTree from './BarnesHutTree.js';

// ZERO-RAF physics engine.
// All simulation is synchronous. No requestAnimationFrame anywhere.
// Layout: run N iterations blocking, draw once.
// Drag: run 1 iteration per pointermove, draw once.
// Idle: literally nothing executes. Zero CPU.

export default class Physics {
  constructor(engine) {
    this._engine = engine;
    this._tree = new BarnesHutTree();

    this._pos = new Float64Array(0);
    this._vel = new Float64Array(0);
    this._force = new Float64Array(0);

    this._idToIndex = new Map();
    this._indexToId = [];
    this._pinned = new Set();
    this._count = 0;

    // Force parameters (match vis-network forceAtlas2Based defaults)
    this._gravitationalConstant = -1000;
    this._centralGravity = 0.01;
    this._springLength = 200;
    this._springConstant = 0.08;
    this._damping = 0.9;
    this._maxVelocity = 15;
    this._timestep = 0.3;

    this._enabled = false;
    this._layoutRunning = false;
  }

  setOptions(opts) {
    if (!opts) return;
    if (opts.maxVelocity !== undefined) this._maxVelocity = opts.maxVelocity;
    if (opts.timestep !== undefined) this._timestep = opts.timestep;
    if (opts.damping !== undefined) this._damping = opts.damping;

    const fa2 = opts.forceAtlas2Based;
    if (fa2) {
      if (fa2.gravitationalConstant !== undefined) this._gravitationalConstant = fa2.gravitationalConstant;
      if (fa2.centralGravity !== undefined) this._centralGravity = fa2.centralGravity;
      if (fa2.springLength !== undefined) this._springLength = fa2.springLength;
      if (fa2.springConstant !== undefined) this._springConstant = fa2.springConstant;
      if (fa2.damping !== undefined) this._damping = fa2.damping;
    }

    const bh = opts.barnesHut;
    if (bh) {
      if (bh.gravitationalConstant !== undefined) this._gravitationalConstant = bh.gravitationalConstant;
      if (bh.centralGravity !== undefined) this._centralGravity = bh.centralGravity;
      if (bh.springLength !== undefined) this._springLength = bh.springLength;
      if (bh.springConstant !== undefined) this._springConstant = bh.springConstant;
      if (bh.damping !== undefined) this._damping = bh.damping;
    }

    if (opts.enabled !== undefined) {
      if (opts.enabled && !this._enabled && !this._layoutRunning) {
        // Enable physics: run full layout synchronously, then self-disable
        this._enabled = true;
        this._layoutSync();
      } else {
        this._enabled = opts.enabled;
      }
    }
  }

  // Run full layout synchronously. Blocks for ~100ms, then one draw, then idle.
  // Self-disables after completion: _enabled → false so next enable triggers layout again.
  _layoutSync() {
    if (this._layoutRunning) return;
    this._layoutRunning = true;

    this.syncFromDataSet();
    if (this._count === 0) {
      this._enabled = false;
      this._layoutRunning = false;
      this._engine.emit('stabilized');
      return;
    }
    for (let i = 0; i < 1000; i++) {
      this._computeForces();
      if (this._integrate() < 0.5) break;
    }
    this._engine.markDirty();

    // Reset before emitting to avoid re-entrance issues
    this._enabled = false;
    this._layoutRunning = false;
    this._engine.emit('stabilized');
  }

  // Called by Interaction on each pointermove during drag.
  // Runs 1 iteration so neighboring nodes respond, then marks dirty for one draw.
  tickOnce() {
    if (this._count === 0) return;
    this._computeForces();
    this._integrate();
    this._engine.markDirty();
  }

  // Synchronous stabilization (for initial graph prop in GraphCanvas)
  stabilize() {
    this.syncFromDataSet();
    if (this._count === 0) return;
    for (let i = 0; i < 1000; i++) {
      this._computeForces();
      if (this._integrate() < 0.5) break;
    }
    this._engine.markDirty();
  }

  // --- Data sync ---

  syncFromDataSet() {
    const nodes = this._engine._nodes;
    const ids = nodes.getIds();
    const newIdSet = new Set(ids);

    for (const [id] of this._idToIndex) {
      if (!newIdSet.has(id)) this._removeNode(id);
    }

    for (let i = 0; i < ids.length; i++) {
      if (!this._idToIndex.has(ids[i])) this._addNode(ids[i]);
    }
  }

  _addNode(id) {
    const idx = this._count++;
    this._idToIndex.set(id, idx);
    this._indexToId[idx] = id;

    const needed = this._count * 2;
    if (this._pos.length < needed) {
      const newSize = Math.max(needed, this._pos.length * 2 || 64);
      const newPos = new Float64Array(newSize);
      const newVel = new Float64Array(newSize);
      const newForce = new Float64Array(newSize);
      newPos.set(this._pos);
      newVel.set(this._vel);
      newForce.set(this._force);
      this._pos = newPos;
      this._vel = newVel;
      this._force = newForce;
    }

    this._pos[idx * 2] = (Math.random() - 0.5) * 1500;
    this._pos[idx * 2 + 1] = (Math.random() - 0.5) * 1500;
    this._vel[idx * 2] = 0;
    this._vel[idx * 2 + 1] = 0;
  }

  _removeNode(id) {
    const idx = this._idToIndex.get(id);
    if (idx === undefined) return;
    const lastIdx = this._count - 1;
    if (idx !== lastIdx) {
      const lastId = this._indexToId[lastIdx];
      this._pos[idx * 2] = this._pos[lastIdx * 2];
      this._pos[idx * 2 + 1] = this._pos[lastIdx * 2 + 1];
      this._vel[idx * 2] = this._vel[lastIdx * 2];
      this._vel[idx * 2 + 1] = this._vel[lastIdx * 2 + 1];
      this._idToIndex.set(lastId, idx);
      this._indexToId[idx] = lastId;
    }
    this._idToIndex.delete(id);
    this._indexToId.length = lastIdx;
    this._count--;
    this._pinned.delete(id);
  }

  pinNode(id) {
    this._pinned.add(id);
    const idx = this._idToIndex.get(id);
    if (idx !== undefined) {
      this._vel[idx * 2] = 0;
      this._vel[idx * 2 + 1] = 0;
    }
  }

  unpinNode(id) {
    this._pinned.delete(id);
  }

  setPosition(id, x, y) {
    const idx = this._idToIndex.get(id);
    if (idx === undefined) return;
    this._pos[idx * 2] = x;
    this._pos[idx * 2 + 1] = y;
  }

  // --- Force computation ---

  _computeForces() {
    const n = this._count;
    if (n === 0) return;

    for (let i = 0; i < n * 2; i++) this._force[i] = 0;

    // Barnes-Hut repulsion (ForceAtlas2-style 1/r)
    this._tree.build(this._pos, n);
    const tf = [0, 0];
    for (let i = 0; i < n; i++) {
      tf[0] = 0; tf[1] = 0;
      this._tree.computeForce(i, this._pos[i * 2], this._pos[i * 2 + 1], tf, this._gravitationalConstant);
      this._force[i * 2] += tf[0];
      this._force[i * 2 + 1] += tf[1];
    }

    // Spring forces (Hooke's law)
    const edges = this._engine._edges.get();
    for (let i = 0; i < edges.length; i++) {
      const e = edges[i];
      const fi = this._idToIndex.get(e.from);
      const ti = this._idToIndex.get(e.to);
      if (fi === undefined || ti === undefined) continue;
      const dx = this._pos[ti * 2] - this._pos[fi * 2];
      const dy = this._pos[ti * 2 + 1] - this._pos[fi * 2 + 1];
      const dist = Math.sqrt(dx * dx + dy * dy) || 0.01;
      const f = this._springConstant * (dist - this._springLength);
      const fx = (f * dx) / dist;
      const fy = (f * dy) / dist;
      this._force[fi * 2] += fx;
      this._force[fi * 2 + 1] += fy;
      this._force[ti * 2] -= fx;
      this._force[ti * 2 + 1] -= fy;
    }

    // Central gravity — constant magnitude toward origin
    for (let i = 0; i < n; i++) {
      const px = this._pos[i * 2];
      const py = this._pos[i * 2 + 1];
      const dist = Math.sqrt(px * px + py * py) || 0.01;
      this._force[i * 2] -= this._centralGravity * px / dist;
      this._force[i * 2 + 1] -= this._centralGravity * py / dist;
    }
  }

  _integrate() {
    const n = this._count;
    const dt = this._timestep;
    const maxV = this._maxVelocity;
    const damping = this._damping;
    let maxSpeed = 0;

    for (let i = 0; i < n; i++) {
      if (this._pinned.has(this._indexToId[i])) {
        this._vel[i * 2] = 0;
        this._vel[i * 2 + 1] = 0;
        continue;
      }
      let vx = (this._vel[i * 2] + this._force[i * 2] * dt) * damping;
      let vy = (this._vel[i * 2 + 1] + this._force[i * 2 + 1] * dt) * damping;
      const speed = Math.sqrt(vx * vx + vy * vy);
      if (speed > maxV) { const s = maxV / speed; vx *= s; vy *= s; }
      if (speed > maxSpeed) maxSpeed = speed;
      this._vel[i * 2] = vx;
      this._vel[i * 2 + 1] = vy;
      this._pos[i * 2] += vx * dt;
      this._pos[i * 2 + 1] += vy * dt;
    }
    return maxSpeed;
  }

  destroy() {
    // Nothing to clean up — no RAF loops exist
  }
}
